import axios from "axios";
import { APP_VERSION } from "../../config/version";

const baseURL = process.env.REACT_APP_BASE_URL;
const invoiceBaseURL = process.env.REACT_APP_INVOICE_BASE_URL;

// Store the last received header version for debugging
let lastHeaderVersion = null;
export const getLastHeaderVersion = () => lastHeaderVersion;

// Function to check app version from response headers (background process)
const checkAppVersion = (response) => {
  const serverVersion = response?.headers?.['x-admin-app-version'];
  
  // Only process if we have a server version
  if (!serverVersion) return;
  
  // Store for debugging
  lastHeaderVersion = serverVersion;
  
  // Log for debugging (can be removed in production)
  console.log(`[Version Check] Header: ${serverVersion}, Local: ${APP_VERSION}, Match: ${serverVersion === APP_VERSION}`);
  
  if (serverVersion !== APP_VERSION) {
    // Version mismatch - dispatch event to show update banner
    window.dispatchEvent(new CustomEvent('app-version-mismatch', {
      detail: { serverVersion, currentVersion: APP_VERSION }
    }));
  } else {
    // Version matches - dispatch event to hide update banner (if shown)
    window.dispatchEvent(new CustomEvent('app-version-match', {
      detail: { serverVersion, currentVersion: APP_VERSION }
    }));
  }
};
// const baseURL = process.env.REACT_APP_DEV_BASE_URL;
// const baseURL = process.env.REACT_APP_LOCAL_URL;

const serialize = function (obj) {
  var str = [];
  for (var p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }
  return str.join("&");
};

const toObject = (str) => {
  let foo = str.split("&").reduce(function (obj, str, index) {
    let strParts = str.split("=");
    if (strParts[0] && strParts[1]) {
      obj[strParts[0].replace(/\s+/g, "")] = strParts[1].trim(); // Get rid of extra spaces at beginning of value strings
    }
    return obj;
  }, {});
  return foo;
};

const updateURL = (query) => {
  let obj = toObject(query);
  for (let param in obj) { // eslint-disable-line no-unused-vars
    if (
      obj[param] === undefined || // In case of undefined assignment
      obj[param] === null ||
      obj[param] === ""
    ) {
      delete obj[param];
    }
  }
  return serialize(obj).toString();
};

// Helper to check if URL is valid for redirect
const isValidRedirectUrl = (url) => {
  if (!url) return false;
  // Don't redirect to login page or empty paths
  const invalidPaths = ['/user-pages/login-1', '/user-pages/login', '/login', '/', ''];
  return !invalidPaths.includes(url);
};

// Function to save intended URL before logout
const saveIntendedUrl = () => {
  const currentPath = window.location.pathname + window.location.search;
  
  // Check if this is the URL we just tried to redirect to after login
  const lastAttemptedRedirect = sessionStorage.getItem('lastAttemptedRedirect');
  if (lastAttemptedRedirect === currentPath) {
    // The redirect we just tried failed - user doesn't have access
    // Mark it as failed so we don't try again
    console.log(`[Redirect] Access denied to ${currentPath} - marking as failed`);
    sessionStorage.setItem('redirectAfterLogin', currentPath);
    sessionStorage.setItem('redirectAttempts', '1'); // Mark as failed once
    sessionStorage.removeItem('lastAttemptedRedirect');
    return;
  }
  
  if (!isValidRedirectUrl(currentPath)) {
    return;
  }
  
  // Check if we already have a saved redirect
  const existingUrl = sessionStorage.getItem('redirectAfterLogin');
  const redirectAttempts = parseInt(sessionStorage.getItem('redirectAttempts') || '0', 10);
  
  if (existingUrl === currentPath) {
    // Same URL is failing again - likely a permissions issue
    if (redirectAttempts >= 1) {
      // Already failed once, clear everything to prevent infinite loop
      console.log(`[Redirect] ${currentPath} failed multiple times - clearing redirect`);
      sessionStorage.removeItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAttempts');
      sessionStorage.removeItem('lastAttemptedRedirect');
      return;
    }
    // Increment attempt counter
    sessionStorage.setItem('redirectAttempts', (redirectAttempts + 1).toString());
  } else if (!existingUrl) {
    // First 401 - save the URL
    console.log(`[Redirect] Saving redirect URL: ${currentPath}`);
    sessionStorage.setItem('redirectAfterLogin', currentPath);
    sessionStorage.setItem('redirectAttempts', '0');
  }
};

// Function to clear local storage and cookies, then reload the page
const handleUnauthorized = () => {
  // Save current URL before clearing session
  saveIntendedUrl();
  
  localStorage.removeItem("user");
  localStorage.removeItem("refreshToken");
  document.cookie = "user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  window.location.reload();
};

// Create axios instances
const axiosInstance = axios.create();
const invoiceAxiosInstance = axios.create();

// Add interceptors to both instances
[axiosInstance, invoiceAxiosInstance].forEach(instance => {
  instance.interceptors.response.use(
    (response) => {
      // Check for app version mismatch on successful responses
      checkAppVersion(response);
      
      // Clear the attempted redirect marker on successful API call
      // This means the user successfully accessed the page
      const lastAttempted = sessionStorage.getItem('lastAttemptedRedirect');
      if (lastAttempted) {
        console.log(`[Redirect] Successfully accessed ${lastAttempted} - clearing redirect tracking`);
        sessionStorage.removeItem('lastAttemptedRedirect');
        sessionStorage.removeItem('redirectAfterLogin');
        sessionStorage.removeItem('redirectAttempts');
      }
      
      return response;
    },
    (error) => {
      // Also check version on error responses (version header is sent on all responses)
      if (error.response) {
        checkAppVersion(error.response);
        
        if (error.response.status === 401) {
          handleUnauthorized();
        }
      }
      return Promise.reject(error);
    }
  );
});

const api = (url = "", method, data = {}, requestConfig = {}) => {
  if (url.includes("?")) {
    const updatedURL1 = url?.split("?")[0];
    const updatedURL2 = url?.split("?")[1];
    const string = updateURL(updatedURL2);
    url = `${updatedURL1}?${string}`;
  }

  const options = {
    method,
    ...requestConfig,
    headers: {
      "content-type": "application/json",
      "x-access-token": `${localStorage.getItem("accessToken")}`,
      ...(requestConfig?.headers || {}),
    },
    data,
    url: `${baseURL}/${url}`,
  };
  return axiosInstance(options);
};

const invoiceApi = (url = "", method, data = {}) => {
  if (url.includes("?")) {
    const updatedURL1 = url?.split("?")[0];
    const updatedURL2 = url?.split("?")[1];
    const string = updateURL(updatedURL2);
    url = `${updatedURL1}?${string}`;
  }

  const options = {
    method,
    headers: {
      "content-type": "application/json",
      "x-access-token": `${localStorage.getItem("accessToken")}`,
      "x-client-type": "admin",
    },
    data,
    url: `${invoiceBaseURL}/${url}`,
  };
  return invoiceAxiosInstance(options);
};

export default api;
export { invoiceApi };

const multipartApi = (url = "", method, data = {}) => {
  if (url.includes("?")) {
    const updatedURL1 = url?.split("?")[0];
    const updatedURL2 = url?.split("?")[1];
    const string = updateURL(updatedURL2);
    url = `${updatedURL1}?${string}`;
  }

  const options = {
    method,
    headers: {
      "content-type": "multipart/form-data",
      "x-access-token": `${localStorage.getItem("accessToken")}`,
      "x-client-type": "admin",
    },
    data,
    url: `${baseURL}/${url}`,
  };
  return axiosInstance(options);
};

export { multipartApi };

const formDataApi = (url = "", method, data = {}) => {
  if (url.includes("?")) {
    const updatedURL1 = url?.split("?")[0];
    const updatedURL2 = url?.split("?")[1];
    const string = updateURL(updatedURL2);
    url = `${updatedURL1}?${string}`;
  }

  const options = {
    method,
    headers: {
      "x-access-token": `${localStorage.getItem("accessToken")}`,
      "x-client-type": "admin",
    },
    data,
    url: `${baseURL}/${url}`,
  };
  return axiosInstance(options);
};

export { formDataApi };

const refreshTokenApi = (url = "", method, data = {}) => {
  const options = {
    method,
    headers: {
      "content-type": "application/json",
      "x-access-token": `${localStorage.getItem("refreshToken")}`,
      "x-client-type": "admin",
    },
    data,
    url: `${baseURL}/${url}`,
  };
  return axiosInstance(options);
};

export { refreshTokenApi };
